import { MAX_PRACTICE_SLOTS } from '../domain/raidPracticeSlots.js';
import { LoadingState } from '../../../shared/ui/LoadingState.jsx';
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshIconButton } from '../../../shared/ui/RefreshIconButton.jsx';
import { useRaidRoomPresence } from '../../raid-control/hooks/useRaidRoomPresence.js';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import {
  adminPracticeDisplayGroups,
  mergePracticeWithActualForAdmin,
} from '../domain/raidPracticeMatcher.js';
import { useRaidPracticeAdmin } from '../hooks/useRaidPracticeAdmin.js';
import { RaidPracticeEditor } from './RaidPracticeEditor.jsx';
import { RaidPracticeRecommendationBoard } from './RaidPracticeRecommendationBoard.jsx';
import { PracticeTableElementFilter, RaidPracticeRecordTable, usePracticeRecordTable } from './RaidPracticeRecordTable.jsx';
import { RaidPracticeSlotCard } from './RaidPracticeSlotCard.jsx';
import { RAID_PRACTICE_ACTION_LABELS, practiceSlotDeleteConfirmation } from './raidPracticeActionLabels.js';
import { readRaidPracticeAdminViewState, writeRaidPracticeAdminViewState } from './raidPracticeAdminViewState.js';

const STATUS_LABELS = { draft: '설정 중', active: '활성', closed: '종료' };
const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: '전체 상태' },
  { value: 'done', label: '실전 3타 완료' },
  { value: 'partial', label: '공격 미완료' },
  { value: 'empty', label: '모의전 미작성' },
  { value: 'input', label: '모의전 입력 있음' },
];
const VIEW_MODE_OPTIONS = [
  { value: 'records', label: '멤버별' },
  { value: 'table', label: '표 보기' },
  { value: 'recommendations', label: '추천 조합' },
];
const SORT_FILTER_OPTIONS = [
  { value: 'default', label: '기본 정렬' },
  { value: 'presence', label: '접속 우선' },
  { value: 'level', label: '레벨순 정렬' },
];

function normalizedText(value) {
  return String(value ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('ko-KR');
}

function memberKey(member = {}) {
  return String(member.openid || member.memberOpenid || member.member_openid || member.nickname || '').trim();
}

function recordMemberKey(record = {}) {
  return String(record.memberOpenid || record.member_openid || '').trim();
}

function recordNickname(record = {}) {
  return String(record.memberNickname || record.member_nickname || record.nickname || '').trim();
}

function sameMember(record, member) {
  const left = recordMemberKey(record);
  const right = memberKey(member);
  if (left && right && left === right) return true;
  const recordName = normalizedText(recordNickname(record));
  const memberName = normalizedText(member?.nickname);
  return Boolean(recordName && memberName && recordName === memberName);
}

// `practiceOnly` builds the same summary while ignoring real attacks, so the board shows the
// registered 모의전 plan itself instead of what the raid replaced it with.
function buildMemberSummary(member, bosses, practiceRecords, actualRecords, practiceOnly = false) {
  const bossEntries = new Map();
  let inputCount = 0;
  const allEntries = [];

  for (const boss of bosses) {
    const manual = practiceRecords.filter((record) => sameMember(record, member) && Number(record.bossNo) === Number(boss.bossNo));
    const actual = practiceOnly ? [] : actualRecords.filter((record) => sameMember(record, member) && Number(record.bossNo) === Number(boss.bossNo));
    const entries = mergePracticeWithActualForAdmin({ manualRecords: manual, actualRecords: actual, bossNo: boss.bossNo });
    inputCount += manual.length;
    allEntries.push(...entries);
    bossEntries.set(Number(boss.bossNo), entries);
  }

  const realCount = allEntries.filter((entry) => Boolean(entry.actual)).length;
  const actualComplete = realCount >= 3;

  if (actualComplete) {
    for (const [bossNo, entries] of bossEntries.entries()) {
      bossEntries.set(bossNo, entries.filter((entry) => Boolean(entry.actual)));
    }
  }

  const usedCount = Math.min(3, allEntries.filter((entry) => Boolean(entry.actual) || Boolean(entry.manual?.used)).length);

  return {
    member,
    bossEntries,
    inputCount,
    usedCount,
    realCount,
    actualComplete,
  };
}

function matchesStatus(summary, status) {
  if (status === 'done') return summary.realCount >= 3;
  if (status === 'partial') return summary.realCount < 3;
  if (status === 'empty') return summary.inputCount === 0;
  if (status === 'input') return summary.inputCount > 0;
  return true;
}

function nextAvailableSlot(entries = []) {
  const usedSlots = new Set(
    entries
      .map((entry) => Number(entry?.manual?.slotNo || 0))
      .filter((slotNo) => slotNo >= 1 && slotNo <= MAX_PRACTICE_SLOTS),
  );
  for (let slotNo = 1; slotNo <= MAX_PRACTICE_SLOTS; slotNo += 1) {
    if (!usedSlots.has(slotNo)) return slotNo;
  }
  return null;
}

function minuteOfDay(date = new Date()) {
  return (date.getHours() * 60) + date.getMinutes();
}

function availabilityContainsMinute(windowValue = {}, currentMinute = minuteOfDay()) {
  const [startHour, startMinute] = String(windowValue.start || windowValue.start_at || '').split(':').map(Number);
  const [endHour, endMinute] = String(windowValue.end || windowValue.end_at || '').split(':').map(Number);
  if (![startHour, startMinute, endHour, endMinute].every(Number.isFinite)) return false;
  const start = (startHour * 60) + startMinute;
  const end = (endHour * 60) + endMinute;
  if (start === end) return false;
  return start < end
    ? currentMinute >= start && currentMinute < end
    : currentMinute >= start || currentMinute < end;
}

function memberAvailableNow(member = {}, currentMinute = minuteOfDay()) {
  return (Array.isArray(member.availabilityWindows) ? member.availabilityWindows : [])
    .some((windowValue) => availabilityContainsMinute(windowValue, currentMinute));
}

function memberSyncLevelValue(member = {}) {
  const value = Number(member.syncLevel ?? member.synchroLevel ?? member.level);
  return Number.isFinite(value) && value > 0 ? Math.trunc(value) : -1;
}

function memberSyncLevel(member = {}) {
  const value = memberSyncLevelValue(member);
  return value > 0 ? value : '-';
}

function PracticeFilterMenu({ ariaLabel, value, options, onChange }) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => String(option.value) === String(value)) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const closeFromOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeFromKeyboard = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeFromOutside);
    document.addEventListener('keydown', closeFromKeyboard);
    return () => {
      document.removeEventListener('pointerdown', closeFromOutside);
      document.removeEventListener('keydown', closeFromKeyboard);
    };
  }, [open]);

  return (
    <div className={`practice-admin-filter-menu ${open ? 'is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="practice-admin-filter-trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selected?.label || ariaLabel}</span>
        <i aria-hidden="true" />
      </button>
      {open ? (
        <div className="practice-admin-filter-options" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => {
            const active = String(option.value) === String(value);
            return (
              <button
                type="button"
                role="option"
                aria-selected={active}
                className={active ? 'is-selected' : ''}
                key={option.value}
                onClick={() => {
                  onChange(String(option.value));
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function PracticeAddSlotButton({ className = '', slotNo, onClick }) {
  return (
    <button
      type="button"
      className={className}
      aria-label={`${slotNo}번 슬롯 추가`}
      title={`${slotNo}번 슬롯 추가`}
      onClick={onClick}
    >
      <span aria-hidden="true">+</span>
      <strong>{RAID_PRACTICE_ACTION_LABELS.add}</strong>
    </button>
  );
}

export function RaidPracticeAdminBoard({ mobile = false, viewSwitch = null }) {
  const admin = useRaidPracticeAdmin();
  const initialViewStateRef = useRef(null);
  if (!initialViewStateRef.current) initialViewStateRef.current = readRaidPracticeAdminViewState();
  const initialViewState = initialViewStateRef.current;
  const [query, setQuery] = useState(initialViewState.query);
  const [statusFilter, setStatusFilter] = useState(initialViewState.statusFilter);
  const [bossFilter, setBossFilter] = useState(initialViewState.bossFilter);
  const [sortMode, setSortMode] = useState(initialViewState.sortMode);
  const [viewMode, setViewMode] = useState(initialViewState.viewMode);
  const [singleBossView, setSingleBossView] = useState(initialViewState.singleBossView);
  const [practiceOnly, setPracticeOnly] = useState(initialViewState.practiceOnly);
  const [expandedMember, setExpandedMember] = useState(initialViewState.expandedMember);
  const [expandedSlotMembers, setExpandedSlotMembers] = useState(() => new Set(initialViewState.expandedSlotMembers));
  const [currentMinute, setCurrentMinute] = useState(() => minuteOfDay());
  const [editor, setEditor] = useState(null);
  const deferredQuery = useDeferredValue(query);
  const deferredStatusFilter = useDeferredValue(statusFilter);
  const deferredBossFilter = useDeferredValue(bossFilter);
  const deferredSortMode = useDeferredValue(sortMode);
  const selectedBossNo = deferredBossFilter === 'all' ? null : Number(deferredBossFilter);
  const showSingleBossOnly = Boolean(selectedBossNo) && singleBossView;
  const season = admin.selectedSeason;
  const canManageAll = ['leader', 'deputy'].includes(String(admin.viewerRole || ''));
  const recommendationMode = viewMode === 'recommendations';
  const tableMode = viewMode === 'table';
  const recordTable = usePracticeRecordTable({ season, query: deferredQuery, enabled: tableMode });
  const viewerUserId = String((season?.members || []).find((member) => member?.isSelf)?.linkedUserId || '');
  const onlinePresence = useRaidRoomPresence({ unionId: admin.unionId, seasonId: season?.id });
  const bossFilterOptions = useMemo(() => [
    { value: 'all', label: '전체 보스 조합' },
    ...(season?.bosses || []).map((boss) => ({ value: String(boss.bossNo), label: `${boss.bossName} 포함` })),
  ], [season?.bosses]);

  useEffect(() => {
    writeRaidPracticeAdminViewState({
      query,
      statusFilter,
      bossFilter,
      sortMode,
      viewMode,
      singleBossView,
      practiceOnly,
      seasonNo: season?.seasonNo || null,
      expandedMember,
      expandedSlotMembers: [...expandedSlotMembers],
    });
  }, [bossFilter, expandedMember, expandedSlotMembers, practiceOnly, query, season?.seasonNo, singleBossView, sortMode, statusFilter, viewMode]);

  useEffect(() => {
    if (sortMode !== 'presence') return undefined;
    const update = () => setCurrentMinute(minuteOfDay());
    update();
    const timer = window.setInterval(update, 30_000);
    return () => window.clearInterval(timer);
  }, [sortMode]);

  const onlineIdentity = useMemo(() => ({
    userIds: new Set((onlinePresence.members || []).map((member) => String(member.userId || '')).filter(Boolean)),
    nicknames: new Set((onlinePresence.members || []).map((member) => normalizedText(member.nickname)).filter(Boolean)),
  }), [onlinePresence.members]);

  const memberIsOnline = (member = {}) => {
    const linkedUserId = String(member.linkedUserId || '').trim();
    if (linkedUserId && onlineIdentity.userIds.has(linkedUserId)) return true;
    return onlineIdentity.nicknames.has(normalizedText(member.nickname));
  };

  const allSummaries = useMemo(() => {
    if (!season) return [];
    const bosses = Array.isArray(season.bosses) ? season.bosses : [];
    const practiceRecords = Array.isArray(season.practiceRecords) ? season.practiceRecords : [];
    const actualRecords = Array.isArray(season.actualRecords) ? season.actualRecords : [];
    return (Array.isArray(season.members) ? season.members : [])
      .map((member) => buildMemberSummary(member, bosses, practiceRecords, actualRecords, practiceOnly));
  }, [practiceOnly, season]);

  const summaries = useMemo(() => {
    if (!season) return [];
    const practiceRecords = Array.isArray(season.practiceRecords) ? season.practiceRecords : [];
    const needle = normalizedText(deferredQuery);
    const activityRank = (summary) => {
      if (memberIsOnline(summary.member)) return 0;
      if (memberAvailableNow(summary.member, currentMinute)) return 1;
      return 2;
    };

    return allSummaries
      .filter((summary) => {
        if (needle && ![summary.member.nickname, memberKey(summary.member)].some((value) => normalizedText(value).includes(needle))) return false;
        if (!matchesStatus(summary, deferredStatusFilter)) return false;
        if (selectedBossNo) {
          const hasSelectedBoss = recommendationMode
            ? practiceRecords.some((record) => sameMember(record, summary.member) && Number(record.bossNo) === selectedBossNo)
            : Boolean((summary.bossEntries.get(selectedBossNo) || []).length);
          if (!hasSelectedBoss) return false;
        }
        return true;
      })
      .sort((a, b) => (deferredSortMode === 'presence' ? activityRank(a) - activityRank(b)
        : deferredSortMode === 'level' ? memberSyncLevelValue(b.member) - memberSyncLevelValue(a.member)
          : 0)
        || b.usedCount - a.usedCount
        || b.realCount - a.realCount
        || b.inputCount - a.inputCount
        || String(a.member.nickname || '').localeCompare(String(b.member.nickname || ''), 'ko'));
  }, [allSummaries, currentMinute, deferredBossFilter, deferredQuery, deferredSortMode, deferredStatusFilter, onlineIdentity, recommendationMode, season]);

  const collapsibleMemberKeys = useMemo(() => {
    if (recommendationMode || !season) return [];
    return allSummaries
      .filter((summary) => adminPracticeDisplayGroups({
        bosses: season.bosses,
        bossEntries: summary.bossEntries,
        actualComplete: summary.actualComplete,
      })
        .filter((group) => !showSingleBossOnly || Number(group.boss?.bossNo) === selectedBossNo)
        .some((group) => (group.entries || []).length > 1))
      .map((summary) => memberKey(summary.member));
  }, [allSummaries, recommendationMode, season, selectedBossNo, showSingleBossOnly]);

  const hasCollapsibleSlots = collapsibleMemberKeys.length > 0;
  const allSlotsExpanded = hasCollapsibleSlots
    && collapsibleMemberKeys.every((key) => expandedSlotMembers.has(key));

  const toggleAllSlots = () => {
    setExpandedSlotMembers(allSlotsExpanded ? new Set() : new Set(collapsibleMemberKeys));
  };

  const toggleMemberSlots = (key) => {
    setExpandedSlotMembers((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openCreateEditor = (member, boss, entries) => {
    const slotNo = nextAvailableSlot(entries);
    const canManageMember = canManageAll || Boolean(member?.isSelf);
    if (!canManageMember || !slotNo) return;
    setEditor({
      mode: 'create',
      entry: { key: `new:${memberKey(member)}:${boss.bossNo}:${slotNo}`, slotNo },
      boss,
      member,
    });
  };

  const deleteFromCard = async (recordId, slotNo) => {
    if (!recordId || !window.confirm(practiceSlotDeleteConfirmation(slotNo))) return false;
    return admin.remove(recordId, slotNo);
  };

  if (admin.loading) return <LoadingState label="멤버 모의전 동기화 중" variant="member" />;
  if (admin.errorMessage) return <section className={`practice-admin-state error ${mobile ? 'mobile' : ''}`}><strong>전체 모의전을 불러오지 못했습니다.</strong><p>{admin.errorMessage}</p><button type="button" onClick={() => admin.refresh()}>다시 시도</button></section>;
  if (!season) return <section className={`practice-admin-state ${mobile ? 'mobile' : ''}`}><strong>표시할 레이드 차수가 없습니다.</strong></section>;

  return (
    <section className={`practice-admin-board ${mobile ? 'mobile' : 'desktop'}`} aria-label="전체 멤버 모의전">
      <div className="practice-admin-heading">
        <div>

          <h2>{recommendationMode ? '멤버 추천 조합' : '전체 멤버 모의전'}</h2>
          {mobile ? <p>{recommendationMode
            ? '멤버별 모의전 기록에서 15명 캐릭터가 겹치지 않는 3타 조합을 합산 딜량 순으로 확인합니다.'
            : tableMode
              ? '등록된 모의전 슬롯을 한 줄씩 모아 딜량과 고점%로 비교합니다.'
            : canManageAll
              ? '멤버별 모의전과 실전 반영 상태를 확인하고, 모의전 기록을 추가·수정·삭제할 수 있습니다.'
              : '유니온 전체 모의전과 실전 반영 상태를 확인합니다. 내 기록만 추가·수정·삭제할 수 있습니다.'}</p> : null}
        </div>
        <div className="practice-admin-season-controls">
          {viewSwitch}
          <div className="practice-admin-mode-switch" role="group" aria-label="보기 방식">
            {VIEW_MODE_OPTIONS.map((option) => (
              <button
                type="button"
                key={option.value}
                className={`practice-admin-mode-button ${viewMode === option.value ? 'active' : ''}`}
                aria-pressed={viewMode === option.value}
                onClick={() => { setEditor(null); setViewMode(option.value); }}
              >
                {option.label}
              </button>
            ))}
          </div>
          <label><span>조회 차수</span><select value={season.seasonNo} onChange={(event) => { setEditor(null); setExpandedMember(''); setExpandedSlotMembers(new Set()); admin.selectSeason(event.target.value); }}>{admin.seasons.map((item) => <option key={item.seasonNo} value={item.seasonNo}>{item.seasonNo}차 · {STATUS_LABELS[item.status] || item.status}</option>)}</select></label>
          <RefreshIconButton onClick={() => admin.refresh(season.seasonNo)} />
        </div>
      </div>

      <div className={`practice-admin-toolbar ${tableMode ? 'table-mode' : ''}`.trim()}>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="멤버 닉네임 검색" />
        {tableMode ? (
          <PracticeTableElementFilter element={recordTable.element} onChange={recordTable.setElement} mobile={mobile} />
        ) : mobile ? (
          <>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">전체 상태</option>
              <option value="done">실전 3타 완료</option>
              <option value="partial">공격 미완료</option>
              <option value="empty">모의전 미작성</option>
              <option value="input">모의전 입력 있음</option>
            </select>
            <select value={bossFilter} onChange={(event) => setBossFilter(event.target.value)}>
              <option value="all">전체 보스 조합</option>
              {season.bosses.map((boss) => <option key={boss.bossNo} value={boss.bossNo}>{boss.bossName} 포함</option>)}
            </select>
            <select value={sortMode} onChange={(event) => setSortMode(event.target.value)} aria-label="멤버 정렬">
              <option value="default">기본 정렬</option>
              <option value="presence">접속 우선</option>
              <option value="level">레벨순 정렬</option>
            </select>
          </>
        ) : (
          <>
            <PracticeFilterMenu
              ariaLabel="상태 필터"
              value={statusFilter}
              options={STATUS_FILTER_OPTIONS}
              onChange={setStatusFilter}
            />
            <PracticeFilterMenu
              ariaLabel="보스 필터"
              value={bossFilter}
              options={bossFilterOptions}
              onChange={setBossFilter}
            />
            <PracticeFilterMenu
              ariaLabel="멤버 정렬"
              value={sortMode}
              options={SORT_FILTER_OPTIONS}
              onChange={setSortMode}
            />
          </>
        )}
        {!recommendationMode && !tableMode ? (
          <div className="practice-admin-view-toggles">
            <label className="practice-admin-single-boss-toggle" title="실전 기록을 빼고 등록된 모의전 슬롯을 그대로 봅니다">
              <input
                type="checkbox"
                checked={practiceOnly}
                onChange={(event) => setPracticeOnly(event.target.checked)}
              />
              <span>모의전만 보기</span>
            </label>
            <label
              className={`practice-admin-single-boss-toggle ${!selectedBossNo ? 'is-disabled' : ''}`.trim()}
              title={selectedBossNo ? '선택한 보스 슬롯만 표시' : '먼저 보스 필터에서 보스를 선택하세요'}
            >
              <input
                type="checkbox"
                checked={singleBossView}
                disabled={!selectedBossNo}
                onChange={(event) => setSingleBossView(event.target.checked)}
              />
              <span>단일 보스만 보기</span>
            </label>
            {hasCollapsibleSlots ? (
              <button type="button" className="practice-admin-collapse-all" aria-pressed={allSlotsExpanded} onClick={toggleAllSlots}>
                {allSlotsExpanded ? '전체 슬롯 접기' : '전체 슬롯 펼치기'}
              </button>
            ) : null}
          </div>
        ) : null}
        {tableMode ? (
          <>
            <button type="button" className="practice-admin-reset" onClick={() => { setQuery(''); recordTable.reset(); }}>초기화</button>
            <strong>{recordTable.visible.length}/{recordTable.rows.length}</strong>
          </>
        ) : (
          <>
            <button type="button" className="practice-admin-reset" onClick={() => { setQuery(''); setStatusFilter('all'); setBossFilter('all'); setSortMode('default'); setSingleBossView(false); setPracticeOnly(false); }}>초기화</button>
            <strong>{summaries.length}/{season.members.length}</strong>
          </>
        )}
      </div>


      {admin.notice.message ? <p className={`practice-admin-notice ${admin.notice.type}`}>{admin.notice.message}</p> : null}

      {tableMode ? (
        <RaidPracticeRecordTable
          rows={recordTable.visible}
          sort={recordTable.sort}
          onSort={recordTable.onSort}
          mobile={mobile}
          canEdit={(row) => Boolean(row.boss && row.record?.id) && (canManageAll || Boolean(row.member?.isSelf))}
          onOpen={(row) => setEditor({
            mode: 'edit',
            entry: { key: row.id, slotNo: row.slotNo, manual: row.record },
            boss: row.boss,
            member: row.member || { nickname: row.nickname, openid: row.record?.memberOpenid },
          })}
        />
      ) : recommendationMode ? (
        <RaidPracticeRecommendationBoard
          members={summaries.map((summary) => summary.member)}
          practiceRecords={season.practiceRecords}
          bosses={season.bosses}
          selectedBossNo={selectedBossNo}
          mobile={mobile}
        />
      ) : (
        <div className={`practice-admin-member-list ${showSingleBossOnly ? 'single-boss-view' : ''}`.trim()}>
          {summaries.map((summary) => {
            const key = memberKey(summary.member);
            const memberExpanded = mobile ? expandedMember === key : true;
            const canManageMember = canManageAll || Boolean(summary.member?.isSelf);
            const displayGroups = adminPracticeDisplayGroups({
              bosses: season.bosses,
              bossEntries: summary.bossEntries,
              actualComplete: summary.actualComplete,
            });
            const visibleDisplayGroups = showSingleBossOnly
              ? displayGroups.filter((group) => Number(group.boss?.bossNo) === selectedBossNo)
              : displayGroups;
            const memberHasCollapsibleSlots = visibleDisplayGroups.some((group) => (group.entries || []).length > 1);
            const memberSlotsExpanded = expandedSlotMembers.has(key);
            const memberIdentity = (
              <div
                className="practice-admin-member-identity"
                style={{ display: 'flex', minWidth: 0, alignItems: 'baseline', gap: '7px' }}
              >
                <h3>{summary.member.nickname || '닉네임 확인 불가'}</h3>
                <small style={{ flex: '0 0 auto', color: 'var(--color-text-dim)', fontSize: mobile ? '9px' : '10px', fontWeight: 900, whiteSpace: 'nowrap' }}>
                  Lv.{memberSyncLevel(summary.member)}
                </small>
              </div>
            );
            const memberBadges = (
              <div className="practice-admin-member-badges">
                <em>입력 {summary.inputCount}개</em>
                <strong>공격완료 {summary.usedCount}/3</strong>
                {mobile ? <b className="practice-admin-member-toggle" aria-hidden="true">{memberExpanded ? '−' : '+'}</b> : null}
                {!mobile && memberHasCollapsibleSlots ? (
                  <button
                    type="button"
                    className="practice-admin-member-slot-toggle"
                    aria-pressed={memberSlotsExpanded}
                    onClick={() => toggleMemberSlots(key)}
                  >
                    {memberSlotsExpanded ? '슬롯 접기' : '슬롯 펼치기'}
                  </button>
                ) : null}
              </div>
            );
            return (
              <article className={`practice-admin-member-card ${summary.usedCount >= 3 ? 'done' : ''} ${summary.actualComplete ? 'actual-complete' : ''} ${mobile && !memberExpanded ? 'is-collapsed' : ''}`} key={key}>
                {mobile ? (
                  <button
                    type="button"
                    className="practice-admin-member-head"
                    aria-expanded={memberExpanded}
                    onClick={() => setExpandedMember(memberExpanded ? '' : key)}
                  >
                    {memberIdentity}
                    {memberBadges}
                  </button>
                ) : (
                  <div className="practice-admin-member-head">
                    {memberIdentity}
                    {memberBadges}
                  </div>
                )}

                {mobile && memberExpanded && memberHasCollapsibleSlots ? (
                  <div className="practice-admin-mobile-slot-controls">
                    <button
                      type="button"
                      className="practice-admin-member-slot-toggle"
                      aria-pressed={memberSlotsExpanded}
                      onClick={() => toggleMemberSlots(key)}
                    >
                      {memberSlotsExpanded ? '슬롯 접기' : '슬롯 펼치기'}
                    </button>
                  </div>
                ) : null}

                {memberExpanded ? (
                  <div
                    className="practice-admin-boss-grid"
                    style={showSingleBossOnly
                      ? { gridTemplateColumns: '1fr' }
                      : !mobile && summary.actualComplete ? { gridTemplateColumns: 'repeat(3,minmax(0,1fr))' } : undefined}
                  >
                    {visibleDisplayGroups.map((group) => {
                      const entries = group.entries || [];
                      const boss = group.boss || {};
                      const availableSlot = nextAvailableSlot(entries);
                      const canAdd = canManageMember && !summary.actualComplete && Boolean(availableSlot);
                      const visibleEntries = entries.length > 1 && !memberSlotsExpanded ? entries.slice(0, 1) : entries;
                      return (
                        <section className="practice-admin-boss-cell" key={group.key}>
                          <div className="practice-admin-boss-head">
                            <div><ElementIcon element={boss.element} /><strong>{boss.bossName}</strong></div>
                            {!summary.actualComplete ? <span>{entries.length ? `슬롯 ${entries.length}` : '미입력'}</span> : null}
                          </div>
                          <div className="practice-admin-slot-stack">
                            {entries.length ? visibleEntries.map((entry) => (
                              <RaidPracticeSlotCard
                                key={entry.key}
                                entry={entry}
                                mobile={mobile}
                                readOnly
                                viewerOpenid={admin.viewerMemberOpenid}
                                canEdit={canManageMember && Boolean(entry.manual?.id)}
                                working={admin.working}
                                onEdit={() => setEditor({ mode: 'edit', entry, boss, member: summary.member })}
                                onDelete={deleteFromCard}
                              />
                            )) : canAdd ? (
                              <PracticeAddSlotButton
                                className={mobile ? 'mobile-practice-empty-slot-v2' : 'desktop-practice-empty-slot-v2'}
                                slotNo={availableSlot}
                                onClick={() => openCreateEditor(summary.member, boss, entries)}
                              />
                            ) : (
                              <div className="practice-admin-empty-slot is-read-only">
                                <b>미입력</b>
                                <small>저장된 모의전 또는 실전 기록 없음</small>
                              </div>
                            )}
                            {entries.length && canAdd ? (
                              <PracticeAddSlotButton className="practice-admin-add-slot" slotNo={availableSlot} onClick={() => openCreateEditor(summary.member, boss, entries)} />
                            ) : null}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                ) : null}
              </article>
            );
          })}
          {!summaries.length ? <div className="practice-admin-no-results"><strong>조건에 맞는 멤버 없음</strong></div> : null}
        </div>
      )}

      {editor ? (
        <RaidPracticeEditor
          entry={editor.entry}
          slotNo={editor.entry?.manual?.slotNo || editor.entry?.slotNo || 1}
          boss={editor.boss}
          seasonNo={season.seasonNo}
          nickname={editor.member?.nickname}
          unionId={admin.unionId}
          mobile={mobile}
          working={admin.working}
          onSave={(payload) => editor.mode === 'create'
            ? admin.create({ member: editor.member, boss: editor.boss, payload })
            : admin.update(editor.entry.manual.id, payload)}
          onDelete={editor.mode === 'edit' ? admin.remove : undefined}
          onClose={() => setEditor(null)}
        />
      ) : null}
    </section>
  );
}
